import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../api';

// The marketing page, shown at "/" to anyone without a session. It commits to the
// dark brand palette rather than following the app's light/dark toggle: the toggle
// lives in the signed-in shell, and a visitor who has never been here has no stored
// preference to honour anyway.

/* ─────────────────────────── the road ───────────────────────────
   One path definition drives three things — the asphalt, the dashed lane line,
   and the cars' motion path — so the traffic can never drift off the road.      */
const ROAD_D = 'M -60 312 C 180 312, 250 132, 520 132 C 790 132, 870 300, 1260 300';

// Half the road's stroke width is 37 user units, so a lane centred 17 either side
// of the dashed line keeps a 12-unit-tall car well inside the asphalt.
const LANE_OFFSET = 17;

// A small green price tag pops near the roadside each time a car runs off the end —
// a sold car leaving the lane. Deliberately a single switch: flip this to false to
// drop it entirely, or delete PRICE_POPS and the <PriceTags/> below, which is the
// whole feature. Nothing else depends on it.
const SHOW_PRICE_POPS = true;

// One per car, so a tag fires exactly as that car leaves — and on the end it leaves
// by: eastbound traffic runs off the right, oncoming off the left.
//
// Positions are expressed against the road rather than as absolute coordinates:
// `along` is a fraction of the path length and `rise` is how far above the centre
// line to sit. The asphalt is 37 units to each side, so a rise above that clears it,
// and the varied values stop the tags stacking at one height. Resolved from the real
// path on mount, so they cannot drift if the curve is ever redrawn — hand-picked
// coordinates had them floating well above it.
const PRICE_POPS = [
  { along: 0.88, rise: 46, dur: 17, delay: 0 },
  { along: 0.94, rise: 62, dur: 17, delay: -17 / 3 },
  { along: 0.82, rise: 78, dur: 17, delay: -(17 * 2) / 3 },
  { along: 0.12, rise: 50, dur: 21, delay: 0 },
  { along: 0.05, rise: 70, dur: 21, delay: -21 / 2 },
];

// Two lanes, as the centre line implies. Two separate things keep cars from driving
// through one another, and both were wrong at first:
//
//   Within a lane, every car shares one duration and is spread by an evenly negative
//   delay. That fixes their spacing permanently. Giving them different durations —
//   the first attempt — means a quicker car eventually laps a slower one.
//
//   Between lanes, the separation comes from `offset-rotate`. Both lanes use the
//   *same* local offset below, which looks like a bug and is not: a car is drawn in
//   the frame offset-rotate gives it, and the oncoming lane's extra 180deg flips
//   that frame, so one identical offset resolves to opposite sides of the dashed
//   line. Mirroring the value by hand — also the first attempt — cancels the flip
//   and puts both lanes on the same side, head-on.
const LANES = [
  {
    dur: 17, reverse: false,
    cars: [
      { fill: '#60a5fa', w: 26, h: 12 },
      { fill: '#e2e8f0', w: 23, h: 11 },
      { fill: '#fbbf24', w: 24, h: 12 },
    ],
  },
  {
    dur: 21, reverse: true,
    cars: [
      { fill: '#94a3b8', w: 24, h: 11 },
      { fill: '#7dd3fc', w: 22, h: 11 },
    ],
  },
];

/**
 * Traffic, drawn *inside* the SVG on purpose.
 *
 * These were HTML divs at first, which put their `offset-path` in CSS pixels while
 * the road scaled with the viewBox — so the cars only sat on the road at exactly
 * 1200px wide and drifted hundreds of pixels away at any other size. On an SVG
 * element the same property resolves in user units, so the traffic now scales with
 * the road it is driving on.
 */
function Traffic() {
  return (
    <>
      {LANES.flatMap((lane, li) =>
        lane.cars.map((c, i) => (
          <g
            key={`${li}-${i}`}
            className="motion-reduce:hidden"
            style={{
              offsetPath: `path("${ROAD_D}")`,
              // `auto` turns the car to face along the curve; the extra 180deg
              // points oncoming traffic the way it is actually travelling.
              offsetRotate: lane.reverse ? 'auto 180deg' : 'auto',
              animation: `${lane.reverse ? 'laneTravelRev' : 'laneTravel'} ${lane.dur}s linear ${
                -(i * lane.dur) / lane.cars.length
              }s infinite`,
            }}
          >
            {/* Drawn about the origin, which sits on the path — so this y offset is
                perpendicular to the road at every point along it. */}
            <rect
              x={-c.w / 2}
              y={-LANE_OFFSET - c.h / 2}
              width={c.w}
              height={c.h}
              rx="3"
              fill={c.fill}
              opacity={lane.reverse ? 0.75 : 1}
            />
            <rect
              x={-c.w / 2 + c.w * 0.55}
              y={-LANE_OFFSET - c.h / 2 + 2}
              width={c.w * 0.3}
              height={c.h - 4}
              rx="1.5"
              fill="#0f172a"
              opacity="0.35"
            />
          </g>
        ))
      )}
    </>
  );
}

/**
 * The price-tag pops. Each one is tied to a car's own duration and delay rather
 * than a timer, so it fires as that car leaves and can never drift out of step
 * with it. Remove by flipping SHOW_PRICE_POPS.
 */
function PriceTags({ roadRef }) {
  const [spots, setSpots] = useState([]);
  useLayoutEffect(() => {
    const path = roadRef.current;
    if (!path?.getTotalLength) return;
    const len = path.getTotalLength();
    // Everything here is SVG user units, which do not change with the viewport —
    // so this resolves once and stays correct at every size.
    setSpots(PRICE_POPS.map(p => {
      const pt = path.getPointAtLength(len * p.along);
      return { x: pt.x, y: pt.y - p.rise };
    }));
  }, [roadRef]);

  if (!spots.length) return null;
  return (
    <>
      {PRICE_POPS.map((p, i) => (
        // Placement and animation are deliberately on two different elements. In
        // SVG the `transform` attribute and the CSS `transform` property are the
        // same property, so an animated CSS transform *replaces* the attribute —
        // put both on one node and the keyframes silently discard the translate,
        // parking every tag at the SVG origin. The outer node owns position and
        // never animates; the inner one owns the pop.
        <g key={i} transform={`translate(${spots[i].x} ${spots[i].y}) rotate(-8)`}>
          <g
            className="motion-reduce:hidden"
            // fill-box so the keyframes' scale grows the tag about itself rather
            // than about the SVG origin, which would fling it off-screen.
            style={{
              transformBox: 'fill-box',
              transformOrigin: 'center',
              animation: `pricePop ${p.dur}s ease-out ${p.delay}s infinite`,
              opacity: 0,
            }}
          >
            <rect x="-15" y="-9" width="30" height="18" rx="4" fill="#10b981" />
            {/* the tag's punch hole */}
            <circle cx="-9" cy="0" r="2.2" fill="#052e1c" opacity="0.55" />
            <text
              x="3.5"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fill="#052e1c"
              fontSize="13"
              fontWeight="800"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              $
            </text>
          </g>
        </g>
      ))}
    </>
  );
}

function HeroRoad() {
  // The tags resolve their own positions from this path, so they always land
  // beside the road rather than at coordinates picked to match it by eye.
  const roadRef = useRef(null);
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[420px] select-none">
      <div className="relative mx-auto h-full w-full max-w-[1200px]">
        <svg viewBox="0 0 1200 400" className="absolute inset-0 h-full w-full" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="fade" x1="0" x2="1">
              <stop offset="0" stopColor="#0b1220" />
              <stop offset="0.16" stopColor="#334155" />
              <stop offset="0.84" stopColor="#334155" />
              <stop offset="1" stopColor="#0b1220" />
            </linearGradient>
            <linearGradient id="laneFade" x1="0" x2="1">
              <stop offset="0" stopColor="#fbbf24" stopOpacity="0" />
              <stop offset="0.2" stopColor="#fbbf24" stopOpacity="0.9" />
              <stop offset="0.8" stopColor="#fbbf24" stopOpacity="0.9" />
              <stop offset="1" stopColor="#fbbf24" stopOpacity="0" />
            </linearGradient>
            {/* Fades the traffic out at both ends of the road, so cars are never
                seen popping into or out of existence mid-frame. */}
            <linearGradient id="trafficFade" x1="0" x2="1">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.12" stopColor="#fff" stopOpacity="1" />
              <stop offset="0.88" stopColor="#fff" stopOpacity="1" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask id="trafficMask">
              <rect x="0" y="0" width="1200" height="400" fill="url(#trafficFade)" />
            </mask>
          </defs>
          <path ref={roadRef} d={ROAD_D} stroke="url(#fade)" strokeWidth="74" strokeLinecap="butt" />
          <path d={ROAD_D} stroke="#0f172a" strokeWidth="74" strokeLinecap="butt" opacity="0.25" />
          <path
            d={ROAD_D}
            stroke="url(#laneFade)"
            strokeWidth="4"
            strokeDasharray="26 22"
            className="animate-laneFlow motion-reduce:animate-none"
          />
          <g mask="url(#trafficMask)">
            <Traffic />
          </g>
          {/* Outside the mask — a tag pops near the road's end, which is exactly
              where the mask fades traffic out. */}
          {SHOW_PRICE_POPS && <PriceTags roadRef={roadRef} />}
        </svg>
      </div>
    </div>
  );
}

/* ─────────────────────── scroll-reveal plumbing ─────────────────────── */

/** Adds a one-shot reveal when the element first scrolls into view. */
function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (or reduced motion): show immediately rather than
    // leaving content invisible, which is the failure mode that actually hurts.
    if (typeof IntersectionObserver === 'undefined'
        || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { rootMargin: '0px 0px -12% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      } ${className}`}
      style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}

/* ───────────────────────────── content ───────────────────────────── */

const STEPS = [
  {
    n: '01',
    title: 'Build the deal',
    body: 'Group cars from your lot into one timed deal. VIN, mileage, condition, grade, photos, reserve and Buy Now — set once, per car.',
  },
  {
    n: '02',
    title: 'Send it where you want',
    body: 'Every lot has a code. A buyer shares theirs, a seller enters it once, and from then on that seller can send them deals. It is not a public auction — nobody sees your inventory unless you send it to them.',
  },
  {
    n: '03',
    title: 'Take offers',
    body: 'Buyers hit Buy Now at your listed price, or open a negotiation. Counters alternate — neither side can bid against themselves — and the whole offer history stays on the car.',
  },
  {
    n: '04',
    title: 'Work it to close',
    body: 'An accepted bid becomes a tracked sale that walks payment, inspection, pickup and title. Each side advances the steps that are theirs, and cancelling takes both parties.',
  },
];

const FEATURES = [
  {
    title: 'Invitation-based, not a public lane',
    body: 'Sellers send deals to lots they choose. Buyers see only what they have been sent — there is no browsing, and no list to appear on.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3z" />
    ),
  },
  {
    title: 'Buy Now and negotiation',
    body: 'List a firm price for an instant sale, a reserve for offers, or both on the same car.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M4 7h16M4 12h10M4 17h13M17 9l3 3-3 3" />
    ),
  },
  {
    title: 'Deal flows that match your terms',
    body: 'Pay first, pay on pickup, or skip inspection. The flow is chosen per deal and locked into every sale it produces.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M5 6h6l2 3h6M5 6v12h14V9M9 13h6" />
    ),
  },
  {
    title: 'Condition on the record',
    body: 'Grade, inspection status, tyres, brakes, drivetrain, disclosures and photos travel with the car into the sale.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M9 12l2 2 4-4M7 4h10a2 2 0 012 2v14l-7-3-7 3V6a2 2 0 012-2z" />
    ),
  },
  {
    title: 'Multiple lots, one account',
    body: 'Run several locations under one dealership, each with its own inventory, tax rate and team.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M4 20V9l5-4 5 4v11M14 20V12l3-2 3 2v8M4 20h16M8 20v-4h2v4" />
    ),
  },
  {
    title: 'Nothing lost between steps',
    body: 'Every bid, counter, payment mark and title hand-off is timestamped, and both sides get notified as it moves.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6"
        d="M12 7v5l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
  },
];

/* A compact, always-animating illustration of a car moving through the sale
   steps — the same sequence the product actually runs. */
const PIPELINE = ['Bid accepted', 'Payment', 'Inspection', 'Pickup', 'Title', 'Closed'];

function Pipeline() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setActive(a => (a + 1) % (PIPELINE.length + 2)), 1100);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-3 sm:gap-x-2">
      {PIPELINE.map((s, i) => {
        const done = i < active;
        const now = i === active;
        return (
          <React.Fragment key={s}>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-500 ${
                now
                  ? 'border-amber-400/70 bg-amber-400/10 text-amber-300'
                  : done
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                  : 'border-slate-700 bg-slate-800/60 text-slate-400'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  now ? 'bg-amber-400 animate-softPulse motion-reduce:animate-none'
                      : done ? 'bg-emerald-400' : 'bg-slate-600'
                }`}
              />
              {s}
            </span>
            {i < PIPELINE.length - 1 && (
              <span className={`h-px w-3 sm:w-5 transition-colors duration-500 ${done ? 'bg-emerald-500/50' : 'bg-slate-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ───────────────────────────── contact ───────────────────────────── */

const EMPTY = { name: '', email: '', company: '', phone: '', kind: 'meeting', message: '' };

function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError('');
    try {
      await api.post('/inquiries', form);
      setState('sent');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center animate-riseIn motion-reduce:animate-none">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15">
          <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="mt-4 text-base font-semibold text-slate-100">Thanks — that reached us.</p>
        <p className="mt-1.5 text-sm text-slate-400">
          We’ll reply to {form.email} shortly.
        </p>
        <button
          type="button"
          onClick={() => { setForm(EMPTY); setState('idle'); }}
          className="mt-5 text-sm font-medium text-blue-400 hover:text-blue-300 hover:underline"
        >
          Send another
        </button>
      </div>
    );
  }

  const field =
    'block w-full rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-2.5 text-base sm:text-sm ' +
    'text-slate-100 placeholder:text-slate-500 transition-colors ' +
    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
  const lbl = 'mb-1.5 block text-xs font-medium text-slate-400';

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 backdrop-blur">
      <div className="mb-5 inline-flex rounded-lg border border-slate-700 bg-slate-900 p-1" role="group" aria-label="What do you need?">
        {[['meeting', 'Request a meeting'], ['question', 'Ask a question']].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setForm(f => ({ ...f, kind: v }))}
            aria-pressed={form.kind === v}
            className={`min-h-[40px] rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${
              form.kind === v ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={lbl} htmlFor="c-name">Name</label>
          <input id="c-name" className={field} value={form.name} onChange={set('name')} required autoComplete="name" />
        </div>
        <div>
          <label className={lbl} htmlFor="c-email">Work email</label>
          <input id="c-email" type="email" className={field} value={form.email} onChange={set('email')} required autoComplete="email" />
        </div>
        <div>
          <label className={lbl} htmlFor="c-company">Dealership <span className="text-slate-600">(optional)</span></label>
          <input id="c-company" className={field} value={form.company} onChange={set('company')} autoComplete="organization" />
        </div>
        <div>
          <label className={lbl} htmlFor="c-phone">Phone <span className="text-slate-600">(optional)</span></label>
          <input id="c-phone" type="tel" className={field} value={form.phone} onChange={set('phone')} autoComplete="tel" />
        </div>
      </div>

      <div className="mt-4">
        <label className={lbl} htmlFor="c-message">
          {form.kind === 'meeting' ? 'What would you like to cover?' : 'What can we help with?'}
        </label>
        <textarea
          id="c-message"
          rows={4}
          className={`${field} resize-y`}
          value={form.message}
          onChange={set('message')}
          required
          placeholder={
            form.kind === 'meeting'
              ? 'A bit about your dealership and the times that suit you.'
              : 'Ask away.'
          }
        />
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={state === 'sending'}
        className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        {state === 'sending' ? (
          <>
            <span className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            Sending…
          </>
        ) : form.kind === 'meeting' ? 'Request a meeting' : 'Send inquiry'}
      </button>
      <p className="mt-3 text-xs text-slate-400">
        We use this to reply to you. Nothing else.
      </p>
    </form>
  );
}

/* ───────────────────────────── page ───────────────────────────── */

export default function Landing() {
  const { login, register } = useAuth();
  const contactRef = useRef(null);

  const toContact = useCallback((e) => {
    e?.preventDefault();
    contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="min-h-dvh overflow-x-hidden bg-slate-950 font-sans text-slate-100">
      {/* The motion-path keyframes live here rather than in the Tailwind config:
          offset-distance is not a theme value, and keeping it beside the element
          it drives makes the pairing obvious. */}
      {/* Kept here rather than in the Tailwind config because offset-distance is
          not a theme value, and the pairing with the element it drives is the
          thing worth keeping obvious. Fading at the road's ends is the mask's job
          now, so these are pure motion — an opacity keyframe here would have to be
          kept in sync with every lane's timing to avoid cars blinking mid-road. */}
      <style>{`
        @keyframes laneTravel    { from { offset-distance:   0%; } to { offset-distance: 100%; } }
        @keyframes laneTravelRev { from { offset-distance: 100%; } to { offset-distance:   0%; } }

        /* Idle for most of the lap, then a quick rise-and-fade as the car it is
           paired with runs off the end. Both ends sit at opacity 0 so the loop
           restart is invisible. */
        @keyframes pricePop {
          0%,  86% { opacity: 0; transform: translateY(4px) scale(0.7); }
          91%      { opacity: 1; transform: translateY(-4px) scale(1); }
          96%      { opacity: 1; transform: translateY(-9px) scale(1); }
          100%     { opacity: 0; transform: translateY(-15px) scale(0.95); }
        }
      `}</style>

      {/* ── header ── */}
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
          <img src="/logo-white.svg" alt="Lane Bids" className="h-7 w-auto" />
          <div className="flex items-center gap-1.5 sm:gap-3">
            <a
              href="#contact"
              onClick={toContact}
              className="hidden rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white sm:inline-flex"
            >
              Contact
            </a>
            <button
              onClick={login}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white"
            >
              Sign in
            </button>
            <button
              onClick={register}
              className="rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
            >
              Create account
            </button>
          </div>
        </div>
      </header>

      {/* ── hero ── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute left-1/2 top-[-14rem] h-[34rem] w-[70rem] -translate-x-1/2 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pb-[22rem] pt-20 sm:px-6 sm:pt-28">
          <div className="max-w-2xl">
            <p className="animate-riseIn motion-reduce:animate-none inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/70 px-3 py-1 text-xs font-medium text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-softPulse motion-reduce:animate-none" />
              Free to buy · by seller invitation
            </p>
            <h1
              className="animate-riseIn motion-reduce:animate-none mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-6xl"
              style={{ animationDelay: '80ms' }}
            >
              Wholesale between dealers you
              <span className="text-blue-400"> actually choose</span>.
            </h1>
            <p
              className="animate-riseIn motion-reduce:animate-none mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
              style={{ animationDelay: '160ms' }}
            >
              Lane Bids is a private wholesale lane for licensed dealerships. Put your cars
              in a timed deal, send it to the lots you pick, take Buy Now or negotiated
              offers, and track every sale through payment, inspection, pickup and title.
            </p>
            <div
              className="animate-riseIn motion-reduce:animate-none mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
              style={{ animationDelay: '240ms' }}
            >
              <button
                onClick={register}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-950"
              >
                Create an account
              </button>
              <button
                onClick={login}
                className="inline-flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900/60 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-600 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                Sign in
              </button>
              <a
                href="#contact"
                onClick={toContact}
                className="inline-flex items-center justify-center gap-1.5 px-2 py-3 text-sm font-semibold text-slate-400 transition-colors hover:text-white"
              >
                Request a meeting
                <span aria-hidden="true">→</span>
              </a>
            </div>
            <p
              className="animate-riseIn motion-reduce:animate-none mt-6 flex items-center gap-2 text-sm text-slate-400"
              style={{ animationDelay: '300ms' }}
            >
              <svg className="h-4 w-4 shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 13l4 4L19 7" />
              </svg>
              <span>
                <span className="font-semibold text-emerald-400">Buying is free</span>
                {' '}— you only pay if you want to sell. Deals reach you when a seller invites your lot.
              </span>
            </p>
            <p
              className="animate-riseIn motion-reduce:animate-none mt-2 text-sm text-slate-400"
              style={{ animationDelay: '320ms' }}
            >
              Ready to sell? Request seller access yourself from Settings once you're signed
              in — no meeting required. "Request a meeting" is there if you'd rather talk it
              through first.
            </p>
          </div>
        </div>
        <HeroRoad />
      </section>

      {/* ── how it works ── */}
      <section className="relative border-t border-slate-800/70 bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">How it works</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              From your lot to a closed sale, in one place.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 110}>
                <div className="relative">
                  {/* The connector doubles as a lane line between steps on wide screens. */}
                  {i < STEPS.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="absolute -right-4 top-3 hidden h-px w-8 lg:block"
                      style={{ backgroundImage: 'repeating-linear-gradient(to right,#475569 0 6px,transparent 6px 12px)' }}
                    />
                  )}
                  <span className="font-mono text-xs font-semibold tracking-widest text-amber-400">{s.n}</span>
                  <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-16 rounded-xl border border-slate-800 bg-slate-900/50 p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                Every sale, tracked end to end
              </p>
              <div className="mt-5">
                <Pipeline />
              </div>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400">
                Buyer and seller each advance the steps that belong to them, and the deal
                flow you chose decides the order — pay up front, or on pickup. Cancelling
                needs both sides to agree.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── features ── */}
      <section className="border-t border-slate-800/70 bg-slate-900/30">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">What you get</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Built for how dealers actually trade.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <div className="group h-full rounded-xl border border-slate-800 bg-slate-900/50 p-6 transition-colors hover:border-slate-700 hover:bg-slate-900">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/80 text-blue-400 transition-colors group-hover:border-blue-500/50 group-hover:text-blue-300">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      {f.icon}
                    </svg>
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── pricing ── */}
      <section className="border-t border-slate-800/70 bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">Pricing</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Buying costs nothing. You only pay to sell.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">
              Every dealership can buy on Lane Bids for free, forever — no subscription, no
              per-deal fee, no card on file. Buying is invitation-based rather than paid: you see
              the deals sellers send your lot, and nothing else. A subscription is only for the
              selling side.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-7">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-white">Buyer</h3>
                  <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300">
                    Free
                  </span>
                </div>
                <p className="mt-3 text-3xl font-extrabold tracking-tight text-white">
                  $0<span className="text-base font-medium text-slate-400"> / month</span>
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Share your lot code and connected sellers send deals straight to you',
                    'Bid, counter, and Buy Now with no fee',
                    'Track your purchases through to title',
                    'Your whole team, no user limit',
                  ].map(t => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                      <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 13l4 4L19 7" />
                      </svg>
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 rounded-lg border border-slate-700/70 bg-slate-950/40 p-3.5 text-xs leading-relaxed text-slate-400">
                  <span className="font-semibold text-slate-200">Access is by invitation.</span>{' '}
                  Your account is free and instant, but deals only appear once a seller adds your
                  lot. Give them the lot code on your settings page and their deals start arriving —
                  until then your list stays empty.
                </p>
                <button
                  onClick={register}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-950"
                >
                  Create a free buyer account
                </button>
              </div>
            </Reveal>

            <Reveal delay={110}>
              <div className="h-full rounded-xl border border-slate-800 bg-slate-900/50 p-7">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-lg font-semibold text-white">Seller</h3>
                  <span className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                    Subscription
                  </span>
                </div>
                <p className="mt-3 text-3xl font-extrabold tracking-tight text-white">
                  $500<span className="text-base font-medium text-slate-400"> / location / month</span>
                </p>
                <p className="mt-1.5 text-xs text-slate-400">
                  Priced per dealership location, not per user. Everyone at the location is covered, buying or selling.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Everything on the buying side, included',
                    'Build timed deals and send them to chosen lots',
                    'Set reserves, Buy Now prices, and the deal flow',
                    'Run several lots under one dealership',
                  ].map(t => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                      <svg className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 13l4 4L19 7" />
                      </svg>
                      {t}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  onClick={toContact}
                  className="mt-7 inline-flex w-full items-center justify-center rounded-lg border border-slate-700 bg-slate-900 px-5 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-slate-600 hover:bg-slate-800"
                >
                  Talk to us about selling
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={160}>
            <p className="mt-8 text-sm text-slate-400">
              Not sure which you need? Start free as a buyer, share your lot code with the dealers
              you already trade with, and add selling later without moving accounts.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── contact ── */}
      <section id="contact" ref={contactRef} className="scroll-mt-16 border-t border-slate-800/70 bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">Get in touch</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Let’s walk through it together.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                Buying is free to start on your own. This is for the selling side — tell us
                about your lot and we’ll walk it end to end: building a deal, sending it out,
                and working an offer to close.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  'A walkthrough with your own inventory in mind',
                  'Straight answers on pricing',
                  'Help connecting the lots you already trade with',
                ].map(t => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 13l4 4L19 7" />
                    </svg>
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-slate-400">
                Already have an account?{' '}
                {/* inline-block padding rather than a min-height: this sits mid-sentence,
                    and a flex box here would break the line it belongs to. */}
                <button onClick={login} className="inline-block py-2 font-semibold text-blue-400 hover:text-blue-300 hover:underline">
                  Sign in
                </button>
                .
              </p>
            </Reveal>

            <Reveal delay={120}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── footer ── */}
      <footer className="border-t border-slate-800/70 bg-slate-950">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-4 py-9 sm:flex-row sm:px-6">
          <img src="/logo-white.svg" alt="Lane Bids" className="h-6 w-auto opacity-80" />
          <div className="flex items-center gap-1 text-sm text-slate-400">
            <a href="/terms" className="inline-flex min-h-[44px] items-center px-2 hover:text-slate-300 hover:underline">Terms</a>
            <span aria-hidden="true">·</span>
            <a href="/privacy" className="inline-flex min-h-[44px] items-center px-2 hover:text-slate-300 hover:underline">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
